import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import logo from "../assets/img/QBits9.webp";

const serviceLinks = [
  {
    to: "/services/ai-automation",
    short: "AI",
    title: "AI Automation",
    description: "Workflow automation, AI agents, and smarter operations.",
  },
  {
    to: "/services/web-mobile-development",
    short: "WM",
    title: "Web & Mobile Development",
    description: "Modern web apps, mobile apps, PWAs, and APIs.",
  },
  {
    to: "/services/saas-erp-development",
    short: "SE",
    title: "SaaS & ERP Development",
    description: "Multi-tenant platforms, ERPs, dashboards, and integrations.",
  },
  {
    to: "/services/desktop-software",
    short: "DS",
    title: "Desktop Software",
    description: "Custom desktop apps, offline tools, and internal systems.",
  },
];

const productLinks = [
  {
    to: "/hospital-management-software",
    short: "HM",
    title: "Hospital Management Software",
    description: "Complete hospital management desktop ERP",
    isNew: true,
  },
  {
    to: "/lumina",
    short: "LU",
    title: "Lumina",
    description: "Salon booking & management platform",
    isNew: true,
  },
];

const NewBadge = () => (
  <span className="lumina-new-badge inline-flex items-center rounded-full bg-primary px-2 py-[2px] text-[10px] font-bold uppercase leading-none text-white">
    NEW
  </span>
);

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showHeader, setShowHeader] = useState(true);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const [isProductsDropdownOpen, setIsProductsDropdownOpen] = useState(false);
  const [isMobileProductsOpen, setIsMobileProductsOpen] = useState(false);

  const lastScrollYRef = useRef(0);
  const tickingRef = useRef(false);
  const servicesDropdownRef = useRef(null);
  const productsDropdownRef = useRef(null);

  const closeAllMenus = useCallback(() => {
    setIsMenuOpen(false);
    setIsServicesDropdownOpen(false);
    setIsMobileServicesOpen(false);
    setIsProductsDropdownOpen(false);
    setIsMobileProductsOpen(false);
  }, []);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (
        servicesDropdownRef.current &&
        !servicesDropdownRef.current.contains(event.target)
      ) {
        setIsServicesDropdownOpen(false);
      }

      if (
        productsDropdownRef.current &&
        !productsDropdownRef.current.contains(event.target)
      ) {
        setIsProductsDropdownOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsServicesDropdownOpen(false);
        setIsProductsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  useEffect(() => {
    if (!isMenuOpen) {
      setIsMobileServicesOpen(false);
      setIsMobileProductsOpen(false);
    }
  }, [isMenuOpen]);

  /* =====================================================
     HEADER SHOW / HIDE ON SCROLL
  ===================================================== */
  useEffect(() => {
    const onScroll = () => {
      if (tickingRef.current) return;
      tickingRef.current = true;

      requestAnimationFrame(() => {
        const current = window.scrollY;
        const last = lastScrollYRef.current;

        if (current < 50) setShowHeader(true);
        else if (current > last) setShowHeader(false);
        else setShowHeader(true);

        lastScrollYRef.current = current;
        tickingRef.current = false;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* =====================================================
     BODY LOCK FOR MOBILE MENU
  ===================================================== */
  useEffect(() => {
    if (isMenuOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
      document.body.style.overflow = "hidden";
    } else {
      const y = document.body.style.top;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      window.scrollTo(0, parseInt(y || "0") * -1);
    }
  }, [isMenuOpen]);

  return (
    <header
      className={`fixed top-0 left-0 w-full z-[999] bg-background border-b border-[#15151526]
      transition-transform duration-500 ease-in-out
      ${showHeader ? "translate-y-0" : "-translate-y-full"}`}
    >
      <div className="max-w-8xl mx-auto flex items-center justify-between py-6 lg:px-16 px-[20px]">
        {/* Logo */}
        <div className="w-36 h-10">
          <Link
            to="/"
            onClick={() => {
              if (window.lenis?.scrollTo) {
                window.lenis.scrollTo(0);
              } else {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
          >
            <img src={logo} alt="QBITS9 – AI Automation Services Company" width="144" height="40" />
          </Link>
        </div>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex flex-1 justify-center" aria-label="Main navigation">
          <ul className="flex space-x-8 items-center">
            <li
              ref={servicesDropdownRef}
              className="relative"
              onMouseEnter={() => setIsServicesDropdownOpen(true)}
              onMouseLeave={() => setIsServicesDropdownOpen(false)}
            >
              <button
                type="button"
                onClick={() => setIsServicesDropdownOpen((open) => !open)}
                className="relative inline-flex items-center gap-2 text-lg text-lightBlack hover:text-primary transition
                after:absolute after:left-0 after:-bottom-0 after:h-[1px] after:w-full after:bg-primary
                after:scale-x-0 after:origin-right after:transition-transform after:duration-300
                hover:after:scale-x-100 hover:after:origin-left"
                aria-haspopup="menu"
                aria-expanded={isServicesDropdownOpen}
              >
                Services
                <svg
                  className={`h-4 w-4 transition-transform duration-300 ${isServicesDropdownOpen ? "rotate-180" : ""}`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M5.25 7.5 10 12.25 14.75 7.5l1.06 1.06L10 14.37 4.19 8.56 5.25 7.5Z" />
                </svg>
              </button>

              <div
                className={`absolute left-1/2 top-full z-[1000] w-[340px] -translate-x-1/2 pt-4 transition-all duration-300 ${isServicesDropdownOpen ? "visible translate-y-0 opacity-100" : "pointer-events-none invisible -translate-y-2 opacity-0"}`}
              >
                <div className="overflow-hidden rounded-[28px] border border-[#15151526] bg-white p-3 shadow-[0_22px_50px_rgba(15,23,42,0.12)]">
                  <div className="space-y-1">
                    {serviceLinks.map((service) => (
                      <Link
                        key={service.to}
                        to={service.to}
                        onClick={closeAllMenus}
                        className="group flex items-start gap-3 rounded-2xl px-4 py-3 transition hover:bg-[#FFF7EF]"
                      >
                        <span className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-sm font-semibold text-primary">
                          {service.short}
                        </span>
                        <span className="min-w-0 text-left">
                          <span className="block text-sm font-semibold text-lightBlack group-hover:text-primary">
                            {service.title}
                          </span>
                          <span className="mt-1 block text-xs leading-5 text-grey">
                            {service.description}
                          </span>
                        </span>
                      </Link>
                    ))}
                  </div>

                  <div className="mt-2 border-t border-[#15151512] pt-2">
                    <Link
                      to="/contact"
                      onClick={closeAllMenus}
                      className="block rounded-2xl bg-primary px-4 py-3 text-center text-sm font-semibold text-white transition hover:bg-secondary"
                    >
                      Explore Service
                    </Link>
                  </div>
                </div>
              </div>
            </li>
            <li
              ref={productsDropdownRef}
              className="relative"
              onMouseEnter={() => setIsProductsDropdownOpen(true)}
              onMouseLeave={() => setIsProductsDropdownOpen(false)}
            >
                <button
                  type="button"
                  onClick={() => setIsProductsDropdownOpen((open) => !open)}
                  className="relative inline-flex items-center gap-2 text-lg text-lightBlack hover:text-primary transition
                after:absolute after:left-0 after:-bottom-0 after:h-[1px] after:w-full after:bg-primary
                after:scale-x-0 after:origin-right after:transition-transform after:duration-300
                hover:after:scale-x-100 hover:after:origin-left"
                  aria-haspopup="menu"
                  aria-expanded={isProductsDropdownOpen}
                >
                  QBits9 Specials
                  <svg
                    className={`h-4 w-4 transition-transform duration-300 ${isProductsDropdownOpen ? "rotate-180" : ""}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M5.25 7.5 10 12.25 14.75 7.5l1.06 1.06L10 14.37 4.19 8.56 5.25 7.5Z" />
                  </svg>
                </button>

                <div
                  className={`absolute left-1/2 top-full z-[1000] w-[340px] -translate-x-1/2 pt-4 transition-all duration-300 ${isProductsDropdownOpen ? "visible translate-y-0 opacity-100" : "pointer-events-none invisible -translate-y-2 opacity-0"}`}
                >
                  <div className="overflow-hidden rounded-[28px] border border-[#15151526] bg-white p-3 shadow-[0_22px_50px_rgba(15,23,42,0.12)]">
                    <div className="space-y-1">
                      {productLinks.map((product) => (
                        <Link
                          key={product.to}
                          to={product.to}
                          onClick={closeAllMenus}
                          className="group flex items-start gap-3 rounded-2xl px-4 py-3 transition hover:bg-[#FFF7EF]"
                        >
                          <span className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-sm font-semibold text-primary">
                            {product.short}
                          </span>
                          <span className="min-w-0 text-left">
                            <span className="flex items-center gap-2 text-sm font-semibold text-lightBlack group-hover:text-primary">
                              {product.title}
                              {product.isNew ? <NewBadge /> : null}
                            </span>
                            <span className="mt-1 block text-xs leading-5 text-grey">
                              {product.description}
                            </span>
                          </span>
                        </Link>
                      ))}
                    </div>

                    <div className="mt-2 border-t border-[#15151512] px-4 pt-3">
                      <p className="text-xs italic leading-5 text-grey">
                        More products coming soon
                      </p>
                    </div>
                  </div>
                </div>
            </li>
          </ul>
        </nav>

        {/* Desktop Button */}
        <div className="hidden lg:block">
          <Link
            to="/contact"
            onClick={closeAllMenus}
            className="relative overflow-hidden flex items-center justify-center bg-primary px-11 py-4 rounded-full group"
          >
            <span className="relative z-10 text-white font-unbounded">
              Book Now
            </span>
            <span className="absolute inset-1/2 w-0 h-0 bg-secondary rounded-full transition-all duration-700
              group-hover:w-[15rem] group-hover:h-[15rem]
              -translate-x-1/2 -translate-y-1/2" />
          </Link>
        </div>

        {/* Hamburger */}
        <button
          className={`hamburger w-6 lg:hidden flex flex-col gap-1 cursor-pointer bg-transparent border-0 p-0 ${isMenuOpen ? "is-active" : ""
            }`}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
          aria-expanded={isMenuOpen}
          aria-controls="mobile-menu"
          type="button"
        >
          <span className="line h-[2px] bg-black w-full"></span>
          <span className="line h-[2px] bg-black w-full"></span>
          <span className="line h-[2px] bg-black w-full"></span>
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        id="mobile-menu"
        aria-hidden={!isMenuOpen}
        className={`fixed left-0 w-full bg-background z-50 rounded-bl-[30px] rounded-br-[30px] transition-all duration-1000 ease-in-out
  overflow-hidden lg:hidden block
        ${isMenuOpen ? "max-h-[75vh] overflow-y-auto " : "max-h-0 "}
  `}
        style={{ top: "89px" }}
      >
        <div className="border-b-2 border-primary rounded-b-[30px] pb-5">
          {/* Mobile Nav */}
          <nav className="p-[20px]" aria-label="Mobile navigation">
            <ul className="space-y-6 text-lg">
              <li>
                <button
                  type="button"
                  onClick={() => setIsMobileServicesOpen((open) => !open)}
                  className="flex w-full items-center justify-between text-left relative inline-block text-lg text-lightBlack hover:text-primary transition
                after:absolute after:left-0 after:-bottom-0
                after:h-[1px] after:w-full after:bg-primary
                after:scale-x-0 after:origin-right
                after:transition-transform after:duration-300
                hover:after:scale-x-100 hover:after:origin-left"
                  aria-expanded={isMobileServicesOpen}
                  aria-controls="mobile-services-dropdown"
                >
                  <span>Services</span>
                  <svg
                    className={`h-4 w-4 transition-transform duration-300 ${isMobileServicesOpen ? "rotate-180" : ""}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M5.25 7.5 10 12.25 14.75 7.5l1.06 1.06L10 14.37 4.19 8.56 5.25 7.5Z" />
                  </svg>
                </button>

                <div
                  id="mobile-services-dropdown"
                  className={`grid transition-all duration-300 ${isMobileServicesOpen ? "mt-4 grid-rows-[1fr] opacity-100" : "mt-0 grid-rows-[0fr] opacity-0"}`}
                >
                  <div className="overflow-hidden">
                    <div className="space-y-3 rounded-[24px] border border-primary/15 bg-white p-4">
                      {serviceLinks.map((service) => (
                        <Link
                          key={service.to}
                          to={service.to}
                          onClick={closeAllMenus}
                          className="block rounded-2xl px-3 py-3 text-base text-lightBlack transition hover:bg-[#FFF7EF] hover:text-primary"
                        >
                          <span className="block font-semibold">{service.title}</span>
                          <span className="mt-1 block text-sm leading-5 text-grey">
                            {service.description}
                          </span>
                        </Link>
                      ))}

                      <Link
                        to="/contact"
                        onClick={closeAllMenus}
                        className="block rounded-2xl bg-primary px-4 py-3 text-center text-sm font-semibold text-white transition hover:bg-secondary"
                      >
                        Get a free audit
                      </Link>
                    </div>
                  </div>
                </div>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => setIsMobileProductsOpen((open) => !open)}
                  className="flex w-full items-center justify-between text-left relative inline-block text-lg text-lightBlack hover:text-primary transition
                after:absolute after:left-0 after:-bottom-0
                after:h-[1px] after:w-full after:bg-primary
                after:scale-x-0 after:origin-right
                after:transition-transform after:duration-300
                hover:after:scale-x-100 hover:after:origin-left"
                  aria-expanded={isMobileProductsOpen}
                  aria-controls="mobile-products-dropdown"
                >
                  <span>QBits9 Specials</span>
                  <svg
                    className={`h-4 w-4 transition-transform duration-300 ${isMobileProductsOpen ? "rotate-180" : ""}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M5.25 7.5 10 12.25 14.75 7.5l1.06 1.06L10 14.37 4.19 8.56 5.25 7.5Z" />
                  </svg>
                </button>

                <div
                  id="mobile-products-dropdown"
                  className={`grid transition-all duration-300 ${isMobileProductsOpen ? "mt-4 grid-rows-[1fr] opacity-100" : "mt-0 grid-rows-[0fr] opacity-0"}`}
                >
                  <div className="overflow-hidden">
                    <div className="space-y-3 rounded-[24px] border border-primary/15 bg-white p-4">
                      {productLinks.map((product) => (
                        <Link
                          key={product.to}
                          to={product.to}
                          onClick={closeAllMenus}
                          className="flex items-start gap-3 rounded-2xl px-3 py-3 text-base text-lightBlack transition hover:bg-[#FFF7EF] hover:text-primary"
                        >
                          <span className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-sm font-semibold text-primary">
                            {product.short}
                          </span>
                          <span className="min-w-0">
                            <span className="flex items-center gap-2 font-semibold">
                              {product.title}
                              {product.isNew ? <NewBadge /> : null}
                            </span>
                            <span className="mt-1 block text-sm leading-5 text-grey">
                              {product.description}
                            </span>
                          </span>
                        </Link>
                      ))}

                      <p className="border-t border-[#15151512] px-3 pt-3 text-xs italic leading-5 text-grey">
                        More products coming soon
                      </p>
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          </nav>

          {/* Mobile Button */}
          <div className="px-[20px]">
            <Link
              to="/contact"
              onClick={closeAllMenus}
              className="relative overflow-hidden flex items-center justify-center
            bg-primary px-11 py-4 rounded-full group"
            >
              <span className="relative z-10 text-white font-unbounded">
                Book Now
              </span>

              <span
                className="absolute inset-1/2 w-0 h-0 bg-secondary rounded-full
            transition-all duration-700 ease-in-out
            group-hover:w-[20rem] group-hover:h-[20rem]
            -translate-x-1/2 -translate-y-1/2"
              ></span>
            </Link>
          </div>
        </div>

      </div>
    </header>
  );
};

export default Header;


// import { useState } from "react";
// import { Link } from "react-router-dom";
// import logo from "../assets/img/QBits9.webp";

// const Header = () => {

//   // mobile menu toggle logic can be added here
//   const [isMenuOpen, setIsMenuOpen] = useState(false);

//   const toggleMenu = () => {
//     setIsMenuOpen(!isMenuOpen);
//   };

//   // useEffect(() => {
//   //   if (isMenuOpen) {
//   //     document.body.style.overflow = "hidden";
//   //   } else {
//   //     document.body.style.overflow = "auto";
//   //   }
//   // }, [isMenuOpen]);

//   return (
//     <header className="header">
//       <div className="max-w-8xl mx-auto flex items-center justify-between py-8 lg:px-16 px-[20px]">
//         {/* Logo */}
//         <div className="w-36 h-10">
//           <Link to="/" className="">
//             <img src={logo} alt="logo" className="" />
//           </Link>
//         </div>
//         {/* Desktop Nav */}
//         <nav className="hidden lg:flex flex-1 justify-center">
//           <ul className="flex space-x-8 text-white text-base font-medium items-center">
//             <li>
//               <Link
//                 to="#"
//                 className="relative inline-block
//              after:absolute after:left-0 after:-bottom-0
//              after:h-[1px] after:w-full after:bg-primary
//              after:scale-x-0 after:origin-right
//              after:transition-transform after:duration-300
//              hover:after:scale-x-100 hover:after:origin-left cursor-pointer text-lg text-lightBlack hover:text-primary font-normal transition"
//               >
//                 Projects
//               </Link>
//             </li>
//             <li>
//               <Link
//                 to="/#faq"
//                 className="relative inline-block
//              after:absolute after:left-0 after:-bottom-0
//              after:h-[1px] after:w-full after:bg-primary
//              after:scale-x-0 after:origin-right
//              after:transition-transform after:duration-300
//              hover:after:scale-x-100 hover:after:origin-left cursor-pointer text-lg text-lightBlack hover:text-primary font-normal transition"
//               >
//                 FAQ
//               </Link>
//             </li>
//           </ul>
//         </nav>
//         <div className="relative hidden lg:block">
//           <a
//             href="/"
//             className="relative overflow-hidden flex items-center justify-center bg-primary transition-colors px-11 py-4 rounded-full font-normal text-base transition duration-300 ease-in-out group"
//           >
//             <span className="flex items-center relative z-10 transition-colors duration-300 group-hover:text-white text-base text-white font-unbounded">
//               Start Project
//             </span>
//             <span className="absolute inset-1/2 w-0 h-0 bg-secondary rounded-full transition-all duration-700 ease-in-out group-hover:w-[15rem] group-hover:h-[15rem] -translate-x-1/2 -translate-y-1/2"></span>
//           </a>
//         </div>

//         <div className="lg:hidden">
//           <button
//             onClick={toggleMenu}
//             className="text-lightBlack focus:outline-none"
//           >
//             <svg
//               className="h-6 w-6"
//               fill="none"
//               stroke="currentColor"
//               viewBox="0 0 24 24"
//               xmlns="http://www.w3.org/2000/svg"
//             >
//               <path
//                 strokeLinecap="round"
//                 strokeLinejoin="round"
//                 strokeWidth={2}
//                 d="M4 6h16M4 12h16M4 18h16"
//               />
//             </svg>
//           </button>
//         </div>

//         <div
//           onClick={() => setIsMenuOpen(false)}
//           className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300
//           ${isMenuOpen ? "opacity-100 visible" : "opacity-0 invisible"}`}
//         ></div>

//         <div className={`fixed top-0 left-0 w-full h-[400px] bg-white z-50 rounded-b-[30px] border-b-2 border-primary
//             transform-gpu will-change-transform
//             transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]
//             ${isMenuOpen ? "translate-y-0" : "-translate-y-[400px]"}
//             lg:hidden`}
//           >
//           <div className="flex justify-between items-center p-[20px]">
//             <div className="w-36 h-10">
//               <Link to="/" className="">
//                 <img src={logo} alt="logo" className="" />
//               </Link>
//             </div>
//             <button
//               onClick={toggleMenu}
//               className="text-lightBlack focus:outline-none"
//             >
//               <svg

//                 className="h-6 w-6"
//                 fill="none"
//                 stroke="currentColor"
//                 viewBox="0 0 24 24"
//                 xmlns="http://www.w3.org/2000/svg"
//               >
//                 <path
//                   strokeLinecap="round"
//                   strokeLinejoin="round"
//                   strokeWidth={2}
//                   d="M6 18L18 6M6 6l12 12"
//                 />
//               </svg>
//             </button>
//           </div>
//           <nav className="p-[20px]">
//             <ul className="space-y-6 text-lg">
//               <li>
//                 <Link
//                   to="#"
//                   className="text-lightBlack hover:text-primary font-normal transition"
//                   onClick={toggleMenu}
//                 >
//                   Projects
//                 </Link>
//               </li>
//               <li>
//                 <Link
//                   to="#"
//                   className="text-lightBlack hover:text-primary font-normal transition"
//                   onClick={toggleMenu}
//                 >
//                   FAQ
//                 </Link>
//               </li>
//             </ul>
//           </nav>
//           <div className="relative w-fit px-[20px] ">
//             <a
//               href="/"
//               className="relative overflow-hidden flex items-center justify-center bg-primary transition-colors px-11 py-4 rounded-full font-normal text-base transition duration-300 ease-in-out group"
//             >
//               <span className="flex items-center relative z-10 transition-colors duration-300 group-hover:text-white text-base text-white font-unbounded">
//                 Start Project
//               </span>
//               <span className="absolute inset-1/2 w-0 h-0 bg-secondary rounded-full transition-all duration-700 ease-in-out group-hover:w-[15rem] group-hover:h-[15rem] -translate-x-1/2 -translate-y-1/2"></span>
//             </a>
//           </div>
//         </div>
//       </div>
//     </header>
//   );
// };

// export default Header;

// import React, { useState, useEffect } from "react";
// import { Link } from "react-router-dom";
// import logo from "../assets/img/QBits9.webp";

// const Header = () => {
//   return (
//     <header className="header">
//       <div className="max-w-8xl mx-auto flex items-center justify-between py-8 md:px-16 px-[20px]">
//         {/* Logo */}
//         <div className="w-36 h-10">
//           <Link to="/" className="">
//             <img src={logo} alt="logo" className="" />
//           </Link>
//         </div>
//         {/* Desktop Nav */}
//         <nav className="hidden lg:flex flex-1 justify-center">
//           <ul className="flex space-x-8 text-white text-base font-medium items-center">
//             <li>
//               <Link
//                 to="#"
//                 className="relative inline-block
//              after:absolute after:left-0 after:-bottom-0
//              after:h-[1px] after:w-full after:bg-primary
//              after:scale-x-0 after:origin-right
//              after:transition-transform after:duration-300
//              hover:after:scale-x-100 hover:after:origin-left cursor-pointer text-lg text-lightBlack hover:text-primary font-normal transition"
//               >
//                 Projects
//               </Link>
//             </li>
//             <li>
//               <Link
//                 to="#"
//                 className="relative inline-block
//              after:absolute after:left-0 after:-bottom-0
//              after:h-[1px] after:w-full after:bg-primary
//              after:scale-x-0 after:origin-right
//              after:transition-transform after:duration-300
//              hover:after:scale-x-100 hover:after:origin-left cursor-pointer text-lg text-lightBlack hover:text-primary font-normal transition"
//               >
//                 FAQ
//               </Link>
//             </li>
//           </ul>
//         </nav>
//         <div className="relative">
//           <a
//             href="/"
//             className="relative overflow-hidden flex items-center justify-center bg-primary transition-colors px-11 py-4 rounded-full font-normal text-base transition duration-300 ease-in-out group"
//           >
//             <span className="flex items-center relative z-10 transition-colors duration-300 group-hover:text-white text-base text-white font-unbounded">
//               Start Project
//             </span>
//             <span className="absolute inset-1/2 w-0 h-0 bg-secondary rounded-full transition-all duration-700 ease-in-out group-hover:w-[15rem] group-hover:h-[15rem] -translate-x-1/2 -translate-y-1/2"></span>
//           </a>
//         </div>
//       </div>
//     </header>
//   );
// };

// export default Header;
